/**
 * FormulaText — рендер пользовательского текста с LaTeX-формулами через KaTeX
 * (self-host, см. импорт CSS в main.tsx). Применяется везде, где выводится текст
 * вопроса/вариантов/эталонов, чтобы ведущий мог писать математику разметкой.
 *
 * Разделители (конвенция LaTeX):
 *  - `$$...$$` — блочная (display) формула;
 *  - `$...$` — строчная (inline) формула.
 *
 * Чтобы одиночный `$` (например, цена «$5») не воспринимался как начало формулы,
 * действует правило: открывающий и закрывающий `$` не должны соседствовать с
 * пробелом, а внутри не должно быть других `$` или переноса строки. Примеры:
 *  - `$x^2$`        → формула;
 *  - `$a/b$`        → формула;
 *  - `$ 5$`, `$5 и $10`, `$5` → обычный текст (не формула).
 *
 * Невалидный LaTeX не рендерится как формула и не падает: при throwOnError:false
 * KaTeX возвращает разметку-заглушку с исходным текстом (класс `.katex-error`);
 * дополнительно обёрнуто в try/catch на крайний случай.
 *
 * Безопасность (XSS): `dangerouslySetInnerHTML` используется ТОЛЬКО для вывода
 * KaTeX, который сам санитизирует вход. Любой текст вне формул рендерится как
 * обычные React-строковые дети (авто-экранирование) — конкатенации сырого HTML
 * нет.
 */
import katex from 'katex';

export interface FormulaTextProps {
  /** Исходный текст, возможно содержащий формулы в разделителях $...$ / $$...$$. */
  text: string;
  /** Доп. класс для корневого элемента (чтобы сохранять существующие стили). */
  className?: string;
}

type Segment =
  | { type: 'text'; value: string }
  | { type: 'inline'; latex: string }
  | { type: 'display'; latex: string };

/**
 * Разбивает текст на сегменты: display-формула ($$...$$), inline-формула ($...$)
 * или обычный текст. Inline-содержимое не начинается/не заканчивается пробелом и
 * не содержит `$`/перенос — это отсекает «доллар-цены» и непарные маркеры.
 */
const FORMULA_RE = /\$\$([\s\S]+?)\$\$|\$([^\s$](?:[^$\n]*[^\s$])?)\$/g;

function parseSegments(text: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Явный сброс на случай переиспользования глобального RegExp.
  FORMULA_RE.lastIndex = 0;
  while ((match = FORMULA_RE.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;
    if (matchStart > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, matchStart) });
    }
    if (match[1] !== undefined) {
      segments.push({ type: 'display', latex: match[1] });
    } else {
      // match[2] — inline-содержимое (группа 2).
      segments.push({ type: 'inline', latex: match[2] });
    }
    lastIndex = matchEnd;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}

/** Рендер LaTeX в HTML через KaTeX; никогда не бросает. */
function renderLatex(latex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(latex, { throwOnError: false, displayMode });
  } catch {
    // Подстраховка: при throwOnError:false KaTeX не бросает, но на всякий случай
    // вернём экранированный исходник, чтобы компонент не ронял рендер.
    return latex.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }
}

export function FormulaText({ text, className }: FormulaTextProps): JSX.Element {
  const segments = parseSegments(text);
  return (
    <span className={className}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          // Текст вне формул — React-экранирование (XSS). Ключ строке не нужен.
          return seg.value;
        }
        const html = renderLatex(seg.latex, seg.type === 'display');
        // HTML только от KaTeX (санитизированный вывод).
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </span>
  );
}
